<template>
  <vs-popup class="holamundo addmore-popup" title="Capture reviews from your platforms" :active.sync="active" @close="active = false">
    <!-- STATUS banner FIRST — tells the user the single most important thing
         (browser supported / extension installed / connected / etc.). The
         Access Key card lives below it because the banner copy refers to
         "the Access Key below". -->
    <div v-if="extensionStatus === 'unsupported_browser'" class="addmore-install-banner mb-3">
      <feather-icon icon="MonitorIcon" svgClasses="w-5 h-5 mr-2 inline align-middle text-warning"/>
      <strong>Your browser doesn't support the Feedback Link extension.</strong>
      Switch to <strong>Google Chrome</strong> (or any Chromium-based browser: Edge, Brave, Arc, Opera, Vivaldi) and open this page there to install it.
      <span class="block mt-1 text-light">Safari, Firefox, and other non-Chromium browsers can't run Chrome extensions.</span>
    </div>
    <div v-else-if="extensionStatus === 'checking'" class="addmore-status-info mb-3">
      <span class="text-sm text-light">Looking for your extension…</span>
    </div>
    <div v-else-if="extensionStatus === 'not_installed'" class="addmore-install-banner mb-3">
      <feather-icon icon="AlertCircleIcon" svgClasses="w-5 h-5 mr-2 inline align-middle text-warning"/>
      <strong>You don't have the Feedback Link extension yet.</strong>
      Click <em>"Get the Feedback Link Chrome extension"</em> below to add it to Chrome (takes about 10 seconds).
      <span class="block mt-1 text-light">Already installed it just now? Refresh this page — Chrome needs a moment to wake the extension up on tabs that were already open.</span>
    </div>
    <div v-else-if="extensionStatus === 'not_signed_in'" class="addmore-install-banner mb-3">
      <feather-icon icon="LogInIcon" svgClasses="w-5 h-5 mr-2 inline align-middle text-warning"/>
      <strong>Almost there — your extension isn't connected to your account yet.</strong>
      Open the Feedback Link extension (click the puzzle-piece icon in your browser toolbar, then <em>Feedback Link</em>), paste the Access Key below into it, and hit <em>Connect</em>. This page will update automatically when you're done — no need to reopen this window.
      <span class="block mt-1 text-light">Tip: pin the extension to your toolbar (click the pin icon next to Feedback Link in the puzzle-piece menu) so you can find it instantly next time.</span>
    </div>
    <div v-else-if="extensionStatus === 'wrong_business'" class="addmore-install-banner mb-3">
      <feather-icon icon="AlertTriangleIcon" svgClasses="w-5 h-5 mr-2 inline align-middle text-warning"/>
      <strong>Your extension is connected to a different business{{ extensionBusinessName ? ' (' + extensionBusinessName + ')' : '' }}.</strong>
      Open the Feedback Link extension (click the puzzle-piece icon in your browser toolbar, then <em>Feedback Link</em>), hit <em>Sign out</em>, then paste the Access Key below. This page will update automatically once it's done.
    </div>
    <div v-else-if="extensionStatus === 'ready'" class="addmore-success-banner mb-3">
      <feather-icon icon="CheckCircleIcon" svgClasses="w-5 h-5 mr-2 inline align-middle text-success"/>
      <strong>You're all set — your Chrome extension is connected{{ extensionBusinessName ? ' to ' + extensionBusinessName : '' }}.</strong>
      Pick a platform below to start adding reviews.
    </div>

    <!-- ACCESS KEY card — auto-shown whenever the user needs it (extension
         missing, not signed in, wrong business). Hidden in the steady "ready"
         state to reduce clutter, revealed on-demand via the "Show Access Key"
         button in the footer. -->
    <div v-if="shouldShowAccessKey" class="addmore-key-card mb-4">
      <p class="text-sm font-semibold mb-1">Your Access Key</p>
      <p class="text-sm text-light mb-2">Copy this and paste it into the Feedback Link Chrome extension to link it to your account. You only need to do this once.</p>
      <div class="flex">
        <vs-input class="flex-grow" readonly :value="$store.getters['dash/devKey']" />
        <vs-button class="ml-2" type="border" icon-pack="feather" :icon="accessKeyCopied ? 'icon-check' : 'icon-copy'" @click="copyAccessKey">{{ accessKeyCopied ? 'Copied!' : 'Copy' }}</vs-button>
      </div>
      <p class="text-sm mt-2">
        <a :href="EXTENSION_STORE_URL" target="_blank" rel="noopener" class="text-primary">
          <feather-icon icon="DownloadIcon" svgClasses="w-4 h-4 inline align-middle mr-1"/>Get the Feedback Link Chrome extension
        </a>
      </p>
      <p class="text-sm mt-1">
        <a href="" @click.prevent="showInstallVideo = true" class="text-primary">
          <feather-icon icon="PlayCircleIcon" svgClasses="w-4 h-4 inline align-middle mr-1"/>Watch the installation walkthrough
        </a>
      </p>
    </div>

    <!-- Platform picker — only meaningful once the extension is fully ready.
         Hiding it on every other state stops users from clicking platform
         cards that would do nothing (or do the wrong thing on wrong_business). -->
    <template v-if="extensionStatus === 'ready'">
      <p class="text-lg mb-2">Pick a platform below — we'll open it in a new tab. Once it loads, click any review on the page to add it to your showcase.</p>

      <div v-if="connectedPlatforms.length === 0" class="text-center py-8">
        <feather-icon icon="AlertCircleIcon" svgClasses="w-8 h-8 text-warning mb-2"/>
        <p class="text-light">No review profiles connected yet.</p>
        <vs-button class="mt-3" @click="goToSharingAndReviews">Connect a profile</vs-button>
      </div>

      <div v-else class="vx-row mt-4">
        <div v-for="p in connectedPlatforms" :key="p.code" class="vx-col w-full md:w-1/2 mb-3">
          <vx-card class="cursor-pointer addmore-card" @click.native="openCaptureUrl(p)">
            <div class="flex items-center">
              <img :src="p.icon" class="w-8 h-8 mr-3 flex-shrink-0" />
              <div class="flex-grow min-w-0">
                <p class="font-semibold">{{ p.name }}</p>
                <p class="text-xs text-light truncate" :title="p.urlDisplay">{{ p.urlDisplay }}</p>
              </div>
              <feather-icon icon="ExternalLinkIcon" svgClasses="w-5 h-5 text-primary ml-2 flex-shrink-0"/>
            </div>
          </vx-card>
        </div>
      </div>

      <div class="text-center mt-4">
        <vs-divider/>
        <p v-if="disconnectedPlatforms.length > 0" class="text-light text-sm mb-3">Have reviews on other platforms too?</p>
        <div class="flex flex-wrap justify-center items-center">
          <vs-button v-if="disconnectedPlatforms.length > 0" type="border" icon-pack="feather" icon="icon-plus" class="mx-1 mb-1" @click="goToSharingAndReviews">Add profiles</vs-button>
          <vs-button type="border" icon-pack="feather" :icon="showAccessKeyOverride ? 'icon-eye-off' : 'icon-eye'" class="mx-1 mb-1" @click="showAccessKeyOverride = !showAccessKeyOverride">{{ showAccessKeyOverride ? 'Hide Access Key' : 'Show Access Key' }}</vs-button>
        </div>
      </div>
    </template>

    <!-- Installation walkthrough video — nested popup, opened from the
         "Watch the installation walkthrough" link in the Access Key card.
         Only mounts the iframe while open so the video doesn't load (or
         autoplay) behind the parent modal. -->
    <vs-popup class="holamundo install-video-popup" title="How to install the Chrome extension" :active.sync="showInstallVideo" @close="showInstallVideo = false">
      <div v-if="showInstallVideo" class="install-video-embed">
        <iframe
          src="https://customer-n548bbvvl783tp8n.cloudflarestream.com/2661e001f507c39acfbbec58bd7b6f04/iframe?muted=true&loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-n548bbvvl783tp8n.cloudflarestream.com%2F2661e001f507c39acfbbec58bd7b6f04%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
          loading="lazy"
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
          allowfullscreen="true"
        ></iframe>
      </div>
    </vs-popup>
  </vs-popup>
</template>

<script>
const PLATFORM_NAMES = {
  gp: 'Google', fb: 'Facebook', tp: 'Trustpilot', ta: 'TripAdvisor',
  ye: 'Yelp', ca: 'Capterra', g2: 'G2', fs: 'Foursquare'
}
// Chrome Web Store listing for the published "Feedback Link – Review Capture"
// extension. Permanent /detail/{id} URL — version updates ship to this same link.
const EXTENSION_STORE_URL = 'https://chromewebstore.google.com/detail/feedback-link-review-capt/lpknajddkbekadhcdpngmeljljcbkdic'

function detectChromium () {
  try {
    const uaData = (typeof navigator !== 'undefined') && navigator.userAgentData
    if (uaData && Array.isArray(uaData.brands)) {
      // Every Chromium browser (Chrome, Edge, Brave, Opera, etc.) reports
      // "Chromium" as one of its brands here.
      if (uaData.brands.some((b) => /chromium/i.test(b.brand))) return true
    }
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || ''
    // iOS browsers all use WebKit and can't run extensions, even if they
    // say "CriOS"/"EdgiOS"/"FxiOS" in their UA.
    if (/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua)) return false
    // Desktop Chromium builds all include "Chrome/" in their UA.
    return /Chrome\//.test(ua)
  } catch (_) {
    return false
  }
}
// Static require map so webpack bundles exactly these 8 files. Don't use
// require(`@/assets/images/logo/${file}`) — that creates a context module
// that scans the entire logo dir (including favicon.ico binary, which
// webpack can't parse).
const PLATFORM_LOGOS = {
  gp: require('@/assets/images/logo/gp.png'),
  fb: require('@/assets/images/logo/fb.png'),
  tp: require('@/assets/images/logo/tp.png'),
  ta: require('@/assets/images/logo/ta.png'),
  ye: require('@/assets/images/logo/ye.png'),
  ca: require('@/assets/images/logo/ca.png'),
  g2: require('@/assets/images/logo/gc.png'),
  fs: require('@/assets/images/logo/fs.png')
}

export default {
  name: 'ImportReviewsModal',
  // v-model: parent controls open/close via :value + @input.
  model: { prop: 'value', event: 'input' },
  props: {
    value: { type: Boolean, default: false }
  },
  data() {
    return {
      // Chromium detection — runs once at instantiation. The extension is
      // Chrome-only (MV3 + the chrome.* extension APIs), so Safari/Firefox
      // users need to be told to switch browsers, not "install the extension".
      // navigator.userAgentData is the reliable signal on modern Chromium
      // builds; UA sniff is a fallback for older Chrome and for Chromium
      // browsers that don't ship userAgentData yet.
      isChromium: detectChromium(),
      // Extension handshake state (pong shape from overlay.js v0.3.0+).
      //   extensionDetected: null = checking, true = pong arrived, false = timeout
      //   extensionSignedIn / extensionBusinessID / extensionBusinessName: only
      //   set when extensionDetected === true. Drives `extensionStatus` computed.
      extensionDetected: null,
      extensionVersion: null,
      extensionSignedIn: false,
      extensionBusinessID: null,
      extensionBusinessName: null,
      EXTENSION_STORE_URL,
      accessKeyCopied: false,
      // The Access Key card auto-shows in non-ready states; in the ready state
      // it's hidden until the user clicks "Show Access Key" in the footer.
      // Reset on modal open via the `value` watcher.
      showAccessKeyOverride: false,
      // Installation walkthrough video popup (nested). Reset on modal open.
      showInstallVideo: false,
      // Held reference to the live message listener (added while the modal is
      // open) so we can remove it when the modal closes.
      extensionLiveListener: null
    }
  },
  computed: {
    active: {
      get() { return this.value },
      set(v) { this.$emit('input', v) }
    },
    // Build step 5 — "Add more" modal data.
    //
    // Per CHROME_EXTENSION_PLAN.md §4c + §10 audit:
    //   - 7 of 8 platforms (fb, tp, ta, ye, ca, g2, fs) store the full profile URL
    //     as `.id` — use directly.
    //   - Google (gp) stores the Place ID as `.id`; the URL is at `.gpObject.url`,
    //     with `https://www.google.com/maps/place/?q=place_id:{id}` as fallback.
    addMorePlatforms() {
      const business = this.$store.state.dash.viewData.business || {}
      const ep = business.externalPlatforms || {}
      const EXTENSION_PLATFORMS = ['gp', 'fb', 'tp', 'ta', 'ye', 'ca', 'g2', 'fs']
      return EXTENSION_PLATFORMS.map(code => {
        const epEntry = ep[code]
        const connected = !!epEntry
          && typeof epEntry === 'object'
          && !!epEntry.id
          && epEntry.fetchReviews !== false
        let profileUrl = null
        let urlDisplay = ''
        if (connected) {
          if (code === 'gp') {
            const gpUrl = epEntry.gpObject && epEntry.gpObject.url
            profileUrl = gpUrl || `https://www.google.com/maps/place/?q=place_id:${epEntry.id}`
            urlDisplay = (epEntry.gpObject && epEntry.gpObject.name)
              || gpUrl
              || `Place ID: ${epEntry.id}`
          } else {
            profileUrl = epEntry.id
            urlDisplay = epEntry.id
          }
        }
        return {
          code,
          name: PLATFORM_NAMES[code] || code,
          icon: PLATFORM_LOGOS[code] || null,
          connected,
          profileUrl,
          urlDisplay
        }
      })
    },
    connectedPlatforms() {
      return this.addMorePlatforms.filter(p => p.connected)
    },
    disconnectedPlatforms() {
      return this.addMorePlatforms.filter(p => !p.connected)
    },
    // Status derived from the browser, the handshake payload, and the
    // currently selected business. Order matters: browser support gates
    // everything, so check that first.
    extensionStatus() {
      if (!this.isChromium) return 'unsupported_browser'
      if (this.extensionDetected === null) return 'checking'
      if (this.extensionDetected === false) return 'not_installed'
      if (!this.extensionSignedIn) return 'not_signed_in'
      const currentBusinessID = this.$store.state.dash.viewData.business
        && this.$store.state.dash.viewData.business.businessID
      if (currentBusinessID && this.extensionBusinessID
          && this.extensionBusinessID !== currentBusinessID) {
        return 'wrong_business'
      }
      return 'ready'
    },
    shouldShowAccessKey() {
      // No point showing the key on browsers that can't run the extension.
      if (this.extensionStatus === 'unsupported_browser') return false
      if (this.showAccessKeyOverride) return true
      return this.extensionStatus !== 'ready'
    }
  },
  watch: {
    value(open) {
      if (open) {
        this.showAccessKeyOverride = false
        this.showInstallVideo = false
        // No point pinging for an extension that can't be installed.
        if (this.isChromium) {
          this.detectExtension()
          this.attachExtensionLiveListener()
        }
      } else {
        this.detachExtensionLiveListener()
      }
    }
  },
  beforeDestroy() {
    this.detachExtensionLiveListener()
  },
  methods: {
    detectExtension() {
      // Handshake with the FBL Chrome extension's content script. Extension
      // overlay (loaded on <all_urls>) listens for window.postMessage
      // {type:'fbl-ping'} and replies with:
      //   {type:'fbl-pong', version, signedIn, businessID, businessName}
      // Absence of reply within 700ms = extension not installed.
      this.extensionDetected = null
      this.extensionVersion = null
      this.extensionSignedIn = false
      this.extensionBusinessID = null
      this.extensionBusinessName = null
      let settled = false
      const onMessage = (ev) => {
        if (ev.source !== window) return
        const d = ev.data
        if (!d || typeof d !== 'object' || d.type !== 'fbl-pong') return
        if (settled) return
        settled = true
        this.extensionDetected = true
        this.extensionVersion = d.version || null
        this.extensionSignedIn = !!d.signedIn
        this.extensionBusinessID = d.businessID || null
        this.extensionBusinessName = d.businessName || null
        window.removeEventListener('message', onMessage)
      }
      window.addEventListener('message', onMessage)
      try {
        window.postMessage({ type: 'fbl-ping' }, window.location.origin)
      } catch (_) { /* origin issues — fall through to timeout */ }
      setTimeout(() => {
        if (settled) return
        settled = true
        this.extensionDetected = false
        window.removeEventListener('message', onMessage)
      }, 700)
    },
    attachExtensionLiveListener() {
      // Listens for unsolicited fbl-pong messages while the modal is open.
      // The extension's content script broadcasts one whenever fbl_auth
      // changes (sign-in / sign-out / business swap), so the status banner
      // updates live without the user reopening the modal.
      if (this.extensionLiveListener) return
      const handler = (ev) => {
        if (ev.source !== window) return
        const d = ev.data
        if (!d || typeof d !== 'object' || d.type !== 'fbl-pong') return
        this.extensionDetected = true
        this.extensionVersion = d.version || null
        this.extensionSignedIn = !!d.signedIn
        this.extensionBusinessID = d.businessID || null
        this.extensionBusinessName = d.businessName || null
      }
      window.addEventListener('message', handler)
      this.extensionLiveListener = handler
    },
    detachExtensionLiveListener() {
      if (!this.extensionLiveListener) return
      window.removeEventListener('message', this.extensionLiveListener)
      this.extensionLiveListener = null
    },
    copyAccessKey() {
      const key = this.$store.getters['dash/devKey']
      if (!key) return
      const done = () => {
        this.accessKeyCopied = true
        setTimeout(() => { this.accessKeyCopied = false }, 2000)
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(key).then(done, () => {
          this.fallbackCopy(key)
          done()
        })
      } else {
        this.fallbackCopy(key)
        done()
      }
    },
    fallbackCopy(text) {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      try { document.execCommand('copy') } catch (_) { /* best-effort */ }
      document.body.removeChild(ta)
    },
    openCaptureUrl(p) {
      // CHROME_EXTENSION_PLAN.md §4c auto-activation protocol:
      //   `#fbl-capture-{businessID}-{timestamp}` — the content script reads
      //   window.location.hash on load, validates businessID, and enables
      //   capture mode silently.
      if (!p || !p.connected || !p.profileUrl) return
      const businessID = this.$store.state.dash.viewData.business && this.$store.state.dash.viewData.business.businessID
      if (!businessID) {
        this.$vs.notify({
          title: 'Capture',
          text: 'Could not determine business ID. Try refreshing the page.',
          color: 'warning',
          time: 6000,
          iconPack: 'feather',
          icon: 'icon-alert-circle'
        })
        return
      }
      const hash = `#fbl-capture-${businessID}-${Date.now()}`
      window.open(`${p.profileUrl}${hash}`, '_blank', 'noopener')
      this.active = false
    },
    goToSharingAndReviews() {
      this.active = false
      this.$router.push('/dash/settings/sharingandreviews')
    }
  }
}
</script>

<style lang="scss">
.addmore-popup .vs-popup {
  max-width: 680px;
}

.addmore-card {
  transition: transform 0.1s ease, box-shadow 0.1s ease;
}
.addmore-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15);
}

.addmore-install-banner {
  background: #fff4e0;
  border: 1px solid #ffbc00;
  border-radius: 6px;
  padding: 12px 14px;
  font-size: 13px;
  color: #5a4400;
  line-height: 1.45;
}

.addmore-success-banner {
  background: #e6f9ed;
  border: 1px solid #28c76f;
  border-radius: 6px;
  padding: 12px 14px;
  font-size: 13px;
  color: #0e6e3a;
  line-height: 1.45;
}

.addmore-key-card {
  background: #faf7ff;
  border: 1px solid #e3d4f5;
  border-radius: 6px;
  padding: 14px 16px;
}
.addmore-key-card .vs-input {
  width: 100%;
}
.addmore-key-card .vs-con-input .vs-inputx {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  letter-spacing: 0.02em;
}

.addmore-status-info {
  padding: 6px 0;
  font-size: 13px;
}

.install-video-popup .vs-popup {
  max-width: 720px;
}
.install-video-embed {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  border-radius: 8px;
  overflow: hidden;
}
.install-video-embed iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}
</style>
